// PATH: src/components/trials/TrialList.tsx
import * as React from "react";
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/ScienceRounded";
import type { Trial, UUID } from "../../models/domain";
import { EmptyState, StatusPill } from "../ui";
import { formatUsd, settingsSummary, trialStatus } from "./trialLogic";

interface Props {
  trials: Trial[];
  subjectNames: Map<UUID, string>;
  onOpen: (trial: Trial) => void;
}

const when = (iso: string) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

/** Les 50 derniers essais : où en est chacun, et ce qu'il a coûté face à l'estimation. */
export const TrialList: React.FC<Props> = ({ trials, subjectNames, onOpen }) => {
  if (!trials.length) {
    return (
      <EmptyState
        dense
        icon={<ScienceIcon />}
        title="Aucun essai pour l'instant"
        description="Choisissez une matière et quelques documents ci-dessus, réglez l'essai, puis lancez-le."
      />
    );
  }
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lancé le</TableCell>
            <TableCell>Matière</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Documents</TableCell>
            <TableCell>Réglages</TableCell>
            <TableCell align="right">Estimé</TableCell>
            <TableCell align="right">Réel</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trials.map((t) => {
            const s = trialStatus(t.status);
            const total = t.document_ids.length;
            return (
              <TableRow key={t.id} hover onClick={() => onOpen(t)} sx={{ cursor: "pointer" }}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{when(t.created_at)}</TableCell>
                <TableCell>{subjectNames.get(t.subject_id) ?? "—"}</TableCell>
                <TableCell>
                  <StatusPill tone={s.tone}>{s.label}</StatusPill>
                </TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                  <Typography variant="body2">
                    {t.documents_done} / {total}
                  </Typography>
                  {t.status === "running" && (
                    <Box sx={{ mt: 0.5 }}>
                      <LinearProgress variant="determinate" value={total ? (100 * t.documents_done) / total : 0} />
                    </Box>
                  )}
                </TableCell>
                <TableCell className="mono" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  {settingsSummary(t.resolved)}
                </TableCell>
                <TableCell align="right">{formatUsd(t.estimate_usd)}</TableCell>
                <TableCell align="right">{formatUsd(t.actual_usd)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TrialList;
